import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Funnel as FunnelIcon, UsersThree, Lightning, Crown, Coins } from "@phosphor-icons/react";

const num = (v) => Number(v || 0).toLocaleString("it-IT");
const eur = (v) => (v == null ? "—" : `€ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? "—" : `${Number(v).toLocaleString("it-IT")}%`);
const dt = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—");

const PERIODS = [
    { label: "Oggi", days: 1 }, { label: "7 giorni", days: 7 }, { label: "30 giorni", days: 30 },
    { label: "90 giorni", days: 90 }, { label: "Tutto", days: 0 },
];
const SEGMENTS = [
    { key: "real", label: "Utenti reali" }, { key: "test", label: "Admin / Test" }, { key: "all", label: "Tutti" },
];

const STATE_COLORS = {
    CLIENTE: "bg-emerald-100 text-emerald-800", CHECKOUT: "bg-blue-100 text-blue-800",
    "INTERESSATO UPGRADE": "bg-violet-100 text-violet-800", ATTIVATO: "bg-cyan-100 text-cyan-800",
    ESPLORATORE: "bg-amber-100 text-amber-800", REGISTRATO: "bg-gray-100 text-gray-700",
    INATTIVO: "bg-rose-100 text-rose-700",
};

function Kpi({ icon: Icon, label, value, sub, testid }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Icon size={16} weight="fill" />
                <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
            </div>
            <div className="font-display-black text-2xl tabular-nums">{value}</div>
            {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
    );
}

const secsLabel = (s) => { // eslint-disable-line no-unused-vars
    if (s == null) return "—";
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
    return `${d}g ${h}h`;
};

export default function Funnel() {
    const { user } = useAuth();
    const [days, setDays] = useState(30);
    const [custom, setCustom] = useState({ from: "", to: "" });
    const [segment, setSegment] = useState("real");
    const [data, setData] = useState(null);
    const [usersRows, setUsersRows] = useState(null);
    const [err, setErr] = useState(false);

    const load = useCallback(() => {
        const params = { segment };
        if (custom.from && custom.to) { params.date_from = custom.from; params.date_to = custom.to; }
        else params.days = days;
        api.get("/admin/funnel/summary", { params }).then(({ data }) => setData(data)).catch(() => setErr(true));
        api.get("/admin/funnel/users", { params: { segment } }).then(({ data }) => setUsersRows(data.users)).catch(() => {});
    }, [days, segment, custom]);

    useEffect(() => { load(); }, [load]);

    const toggleTest = async (row) => {
        await api.patch(`/admin/funnel/users/${row.user_id}/test-flag`, { is_test_account: !row.is_test }).catch(() => {});
        load();
    };

    if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

    const steps = data?.steps || [];
    const maxCount = Math.max(1, ...steps.map((s) => s.count));
    const reg = steps.find((s) => s.key === "registered")?.count || 0;
    const paid = steps.find((s) => s.key === "paid")?.count || 0;
    const activated = steps.find((s) => s.key === "analysis_completed")?.count || 0;

    return (
        <Layout>
            <div className="space-y-6" data-testid="admin-funnel">
                <div>
                    <h1 className="font-display-black text-3xl flex items-center gap-2">
                        <FunnelIcon size={26} weight="fill" /> Funnel conversione
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Percorso reale visitatore → cliente pagante, costruito solo sui dati del database.
                        Le conversioni sono su utenti unici. Nessun servizio esterno.
                    </p>
                </div>

                {/* Filtri */}
                <div className="flex flex-wrap items-center gap-2" data-testid="funnel-filters">
                    {PERIODS.map((p) => (
                        <button key={p.label} onClick={() => { setCustom({ from: "", to: "" }); setDays(p.days); }}
                                data-testid={`funnel-period-${p.days}`}
                                className={`px-3.5 h-9 text-xs font-bold rounded-lg border transition-colors ${
                                    !custom.from && days === p.days ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-gray-300 text-gray-600 hover:border-gray-500"}`}>
                            {p.label}
                        </button>
                    ))}
                    <div className="flex items-center gap-1.5 ml-1">
                        <input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                               data-testid="funnel-date-from" className="h-9 px-2 text-xs border border-gray-300 rounded-lg bg-transparent" />
                        <span className="text-xs text-gray-400">→</span>
                        <input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                               data-testid="funnel-date-to" className="h-9 px-2 text-xs border border-gray-300 rounded-lg bg-transparent" />
                    </div>
                    <div className="flex items-center gap-1 ml-auto border border-gray-300 rounded-lg p-1" data-testid="funnel-segment">
                        {SEGMENTS.map((s) => (
                            <button key={s.key} onClick={() => setSegment(s.key)} data-testid={`funnel-segment-${s.key}`}
                                    className={`px-3 h-7 text-[11px] font-bold rounded-md transition-colors ${
                                        segment === s.key ? "bg-[#0A0A0A] text-white" : "text-gray-500 hover:text-gray-800"}`}>
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                {err && <p className="text-sm text-rose-600" data-testid="funnel-error">Impossibile caricare i dati del funnel.</p>}

                {data && (
                    <>
                        {/* KPI */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="funnel-kpi">
                            <Kpi icon={UsersThree} label="Registrazioni" value={num(reg)}
                                 sub={`${num(steps[0]?.count)} visite nel periodo`} testid="funnel-kpi-reg" />
                            <Kpi icon={Lightning} label="Attivati (1ª analisi)" value={num(activated)}
                                 sub={reg ? `${Math.round((activated / reg) * 100)}% dei registrati` : "—"} testid="funnel-kpi-activated" />
                            <Kpi icon={Crown} label="Paganti" value={num(paid)}
                                 sub={pct(data.economics.reg_to_paid_pct) + " registrato → pagante"} testid="funnel-kpi-paid" />
                            <Kpi icon={Coins} label="MRR" value={eur(data.economics.mrr_eur)}
                                 sub={`${num(data.economics.paying_users)} utenti paganti attivi`} testid="funnel-kpi-mrr" />
                        </div>

                        {/* Funnel */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid="funnel-steps">
                            <h3 className="font-display-black text-base mb-4">Funnel principale · {data.range.from} → {data.range.to}</h3>
                            <div className="space-y-1.5">
                                {steps.map((s, i) => (
                                    <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                                        {i > 0 && s.dropoff > 0 && (
                                            <div className="pl-[260px] py-0.5 text-[11px] text-rose-500 font-medium hidden md:block">
                                                ↓ drop-off: {num(s.dropoff)} ({pct(s.dropoff_pct)})
                                            </div>
                                        )}
                                        <div className="flex items-center gap-3">
                                            <div className="w-full md:w-[248px] shrink-0 text-[13px] font-semibold text-gray-800 flex items-center gap-1.5">
                                                {s.label}
                                                {s.historical === "non_disponibile" && <span className="text-[9px] font-bold text-gray-500 border border-gray-300 rounded px-1">STORICO N/D</span>}
                                                {s.historical === "parziale" && <span className="text-[9px] font-bold text-amber-600 border border-amber-300 rounded px-1" title={`tracciato dal ${dt(s.tracked_from)} — lo storico precedente non è disponibile`}>TRACCIATO DAL {dt(s.tracked_from)}</span>}
                                                {s.source === "ricostruito" && <span className="text-[9px] font-bold text-emerald-700 border border-emerald-300 rounded px-1">RICOSTRUITO</span>}
                                            </div>
                                            <div className="flex-1 hidden md:block">
                                                <div className="h-6 bg-gray-100 rounded overflow-hidden">
                                                    <div className={`h-full ${s.key === "paid" ? "bg-emerald-600" : s.key === "visits" ? "bg-gray-400" : "bg-[#3B66F5]"}`}
                                                         style={{ width: `${Math.max(1, Math.round((s.count / maxCount) * 100))}%` }} />
                                                </div>
                                            </div>
                                            <div className="w-16 text-right font-mono-tight font-bold tabular-nums">{num(s.count)}</div>
                                            <div className="w-16 text-right text-xs text-gray-500 tabular-nums">{s.conversion_pct != null ? pct(s.conversion_pct) : ""}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Drop-off */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid="funnel-dropoff">
                            <h3 className="font-display-black text-base mb-3">Dove perdiamo gli utenti?</h3>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {steps.slice(1).filter((s) => s.dropoff != null).map((s, i) => (
                                    <div key={s.key} className={`border rounded-lg p-3 ${s.dropoff_pct >= 60 ? "border-rose-300 bg-rose-50" : "border-gray-200"}`}>
                                        <div className="text-[11px] text-gray-500">{steps[i].label} → {s.label}</div>
                                        <div className="font-display-black text-lg tabular-nums mt-0.5">
                                            −{num(s.dropoff)} <span className={`text-sm ${s.dropoff_pct >= 60 ? "text-rose-600" : "text-gray-500"}`}>({pct(s.dropoff_pct)})</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Cohort + tempo alla prima analisi */}
                        <div className="grid lg:grid-cols-2 gap-4">
                            <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-x-auto" data-testid="funnel-cohorts">
                                <div className="p-5 pb-0"><h3 className="font-display-black text-base">Cohort per settimana di registrazione</h3></div>
                                <table className="w-full text-sm mt-3 min-w-[520px]">
                                    <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                                        <th className="px-5 py-2">Settimana</th><th className="px-2 py-2">Registrati</th>
                                        <th className="px-2 py-2">1ª analisi</th><th className="px-2 py-2">Attivazione</th>
                                        <th className="px-2 py-2">T. medio</th><th className="px-2 py-2">T. mediano</th>
                                        <th className="px-2 py-2">&gt;1 analisi</th>
                                        <th className="px-2 py-2">Upgrade</th><th className="px-2 py-2">Checkout</th><th className="px-4 py-2">Paganti</th>
                                    </tr></thead>
                                    <tbody>
                                        {data.cohorts.map((w) => (
                                            <tr key={w.week_start} className="border-b border-gray-50">
                                                <td className="px-5 py-2 font-medium whitespace-nowrap">dal {dt(w.week_start)}</td>
                                                <td className="px-2 py-2 tabular-nums">{w.registered}</td>
                                                <td className="px-2 py-2 tabular-nums">{w.first_analysis}</td>
                                                <td className="px-2 py-2 tabular-nums font-semibold">{pct(w.activation_pct)}</td>
                                                <td className="px-2 py-2 tabular-nums">{w.avg_hours_to_first != null ? `${w.avg_hours_to_first}h` : "—"}</td>
                                                <td className="px-2 py-2 tabular-nums">{w.median_hours_to_first != null ? `${w.median_hours_to_first}h` : "—"}</td>
                                                <td className="px-2 py-2 tabular-nums">{w.multi_analysis}</td>
                                                <td className="px-2 py-2 tabular-nums">{w.upgrade}</td>
                                                <td className="px-2 py-2 tabular-nums">{w.checkout}</td>
                                                <td className="px-4 py-2 tabular-nums font-semibold text-emerald-700">{w.paying}</td>
                                            </tr>
                                        ))}
                                        {!data.cohorts.length && <tr><td colSpan={10} className="px-5 py-6 text-center text-gray-500">Nessuna registrazione nel periodo.</td></tr>}
                                    </tbody>
                                </table>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid="funnel-time-to-first">
                                <h3 className="font-display-black text-base mb-3">Tempo registrazione → prima analisi</h3>
                                <div className="flex gap-8 mb-4">
                                    <div><div className="text-[11px] text-gray-500 uppercase">Media</div>
                                        <div className="font-display-black text-xl tabular-nums">{data.time_to_first_analysis.avg_hours != null ? `${data.time_to_first_analysis.avg_hours} h` : "—"}</div></div>
                                    <div><div className="text-[11px] text-gray-500 uppercase">Mediana</div>
                                        <div className="font-display-black text-xl tabular-nums">{data.time_to_first_analysis.median_hours != null ? `${data.time_to_first_analysis.median_hours} h` : "—"}</div></div>
                                </div>
                                {[["lt_1h", "< 1 ora"], ["h1_24", "1–24 ore"], ["d1_3", "1–3 giorni"], ["d3_7", "3–7 giorni"], ["over_7d", "> 7 giorni"], ["mai", "Mai"]].map(([k, label]) => {
                                    const v = data.time_to_first_analysis.distribution[k] || 0;
                                    const tot = Math.max(1, reg);
                                    return (
                                        <div key={k} className="flex items-center gap-2 py-1">
                                            <div className="w-20 text-xs text-gray-600">{label}</div>
                                            <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                                                <div className={`h-full ${k === "mai" ? "bg-rose-400" : "bg-[#22D3EE]"}`} style={{ width: `${Math.round((v / tot) * 100)}%` }} />
                                            </div>
                                            <div className="w-8 text-right text-xs tabular-nums font-semibold">{v}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Blocco piano + Economics */}
                        <div className="grid lg:grid-cols-2 gap-4">
                            <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid="funnel-plan-lock">
                                <div className="flex items-baseline justify-between mb-3">
                                    <h3 className="font-display-black text-base">Blocco piano → Checkout</h3>
                                    {data.plan_lock?.tracked_from && (
                                        <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                                            tracciato dal {dt(data.plan_lock.tracked_from)}
                                        </span>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="border border-gray-200 rounded-lg p-3" data-testid="plan-lock-viewed">
                                        <div className="text-[11px] text-gray-500">Hanno visto il blocco</div>
                                        <div className="font-display-black text-2xl tabular-nums text-blue-700">{num(data.plan_lock?.viewed)}</div>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-3" data-testid="plan-lock-checkout">
                                        <div className="text-[11px] text-gray-500">Arrivati al checkout</div>
                                        <div className="font-display-black text-2xl tabular-nums text-amber-700">{num(data.plan_lock?.checkout)}</div>
                                        <div className="text-[11px] text-gray-400 tabular-nums">{pct(data.plan_lock?.view_to_checkout_pct)} dei bloccati</div>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-3" data-testid="plan-lock-paid">
                                        <div className="text-[11px] text-gray-500">Hanno pagato</div>
                                        <div className="font-display-black text-2xl tabular-nums text-emerald-700">{num(data.plan_lock?.paid)}</div>
                                        <div className="text-[11px] text-gray-400 tabular-nums">{pct(data.plan_lock?.view_to_paid_pct)} dei bloccati</div>
                                    </div>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-3">
                                    Utenti unici (nel periodo) che hanno visto l&apos;overlay &quot;Attiva un piano per continuare&quot; e quanti di loro hanno poi avviato un checkout Stripe o completato il pagamento.
                                </p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid="funnel-economics">
                                <h3 className="font-display-black text-base mb-3">Economics</h3>
                                <table className="w-full text-sm">
                                    <tbody>
                                        {[["Analisi effettuate", num(data.economics.analyses)],
                                          ["Costo AI (segmento)", eur(data.economics.ai_cost_eur)],
                                          ["Costo AI totale", eur(data.economics.ai_breakdown?.total)],
                                          ["— di cui Admin/Test", eur(data.economics.ai_breakdown?.test)],
                                          ["— di cui utenti Free", eur(data.economics.ai_breakdown?.free)],
                                          ["— di cui utenti paganti", eur(data.economics.ai_breakdown?.paying)],
                                          ["— non attribuibile (storico)", eur(data.economics.ai_breakdown?.unattributed)],
                                          ["Costo AI / analisi", eur(data.economics.ai_cost_per_analysis)],
                                          ["Costo AI / registrato", eur(data.economics.ai_cost_per_registered)],
                                          ["Costo AI / attivato", eur(data.economics.ai_cost_per_activated)],
                                          ["Ricavi abbonamenti (periodo)", eur(data.economics.revenue_eur)],
                                          ["MRR", eur(data.economics.mrr_eur)],
                                          ["Utenti paganti", num(data.economics.paying_users)],
                                          ["Registrato → pagante", pct(data.economics.reg_to_paid_pct)]].map(([l, v]) => (
                                            <tr key={l} className="border-b border-gray-50">
                                                <td className="py-1.5 text-gray-600">{l}</td>
                                                <td className="py-1.5 text-right font-semibold tabular-nums">{v}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Tabella attivazione utenti */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-x-auto" data-testid="funnel-users-table">
                            <div className="p-5 pb-0 flex items-baseline justify-between">
                                <h3 className="font-display-black text-base">Attivazione utenti ({usersRows?.length ?? "…"})</h3>
                                <span className="text-[11px] text-gray-400">Il toggle Test esclude l&apos;account dalle metriche commerciali</span>
                            </div>
                            <table className="w-full text-xs mt-3 min-w-[1500px]">
                                <thead><tr className="text-left text-[10px] uppercase text-gray-500 border-b border-gray-200">
                                    {["Utente", "Stato", "Registrato", "Ultimo accesso", "Gg", "Radar", "Aste viste",
                                      "1ª analisi", "Data 1ª", "N. analisi", "Ultima analisi", "Risultato visto", "Upgrade", "Checkout",
                                      "Piano", "Pagamento", "Ultima attività", "Test"].map((h) => (
                                        <th key={h} className="px-2.5 py-2 whitespace-nowrap">{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {(usersRows || []).map((r) => (
                                        <tr key={r.user_id} className="border-b border-gray-50" data-testid={`funnel-user-${r.user_id}`}>
                                            <td className="px-2.5 py-2 whitespace-nowrap"><div className="font-semibold">{r.full_name || "—"}</div><div className="text-gray-400">{r.email}</div></td>
                                            <td className="px-2.5 py-2"><span className={`px-2 py-0.5 rounded-full font-bold text-[10px] whitespace-nowrap ${STATE_COLORS[r.state] || "bg-gray-100"}`}>{r.state}</span></td>
                                            <td className="px-2.5 py-2 whitespace-nowrap">{dt(r.created_at)}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap">{r.last_login_at ? dt(r.last_login_at) : "dato non disp."}</td>
                                            <td className="px-2.5 py-2 tabular-nums">{r.days_since_registration}</td>
                                            <td className="px-2.5 py-2">{r.radar_configured ? "Sì" : "No"}</td>
                                            <td className="px-2.5 py-2 tabular-nums">{r.auctions_viewed}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap">{r.first_analysis_status}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap">{dt(r.first_analysis_at)}</td>
                                            <td className="px-2.5 py-2 tabular-nums">{r.analyses_total}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap">{dt(r.last_analysis_at)}</td>
                                            <td className="px-2.5 py-2">{r.result_viewed ? "Sì" : "No"}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap">{r.upgrade_clicked ? `Sì · ${dt(r.last_upgrade_click_at)}` : "No"}</td>
                                            <td className="px-2.5 py-2 capitalize">{r.checkout}</td>
                                            <td className="px-2.5 py-2 capitalize font-semibold">{r.plan_label}</td>
                                            <td className="px-2.5 py-2 capitalize">{r.subscription_status}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap">{dt(r.last_activity_at)}</td>
                                            <td className="px-2.5 py-2">
                                                <button onClick={() => toggleTest(r)} data-testid={`funnel-test-toggle-${r.user_id}`}
                                                        className={`px-2 py-0.5 rounded font-bold text-[10px] border transition-colors ${
                                                            r.is_test ? "bg-amber-100 text-amber-800 border-amber-300" : "text-gray-400 border-gray-300 hover:border-gray-500"}`}>
                                                    {r.is_test ? "TEST" : "reale"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {usersRows && !usersRows.length && <tr><td colSpan={20} className="px-5 py-6 text-center text-gray-500">Nessun utente nel segmento.</td></tr>}
                                </tbody>
                            </table>
                        </div>

                        <p className="text-[11px] text-gray-400">
                            Passi contrassegnati con data: eventi tracciati solo da quella data (lo storico precedente non è ricostruibile e non viene inventato).
                            Pagamento = conferma reale Stripe (plan_activated), mai il semplice click sul bottone.
                        </p>
                    </>
                )}
            </div>
        </Layout>
    );
}
